/**
 * Customer Notification System
 *
 * Translates internal job milestones into customer-friendly progress updates.
 * Provides notification generation, status translation, and simulated
 * email/SMS delivery. No internal jargon is exposed to customers.
 */

import type { Job, JobStage } from "./types";
import type { ChangeOrder } from "./changeOrderTypes";
import { getStageConfig, getOrderedStages } from "./workflowEngine";

/* ═══════════════════════════════════════════════════
   Milestone definitions
   ═══════════════════════════════════════════════════ */

export type MilestoneType =
  | "contract_signed"
  | "project_preparation"
  | "materials_finalized"
  | "installation_scheduled"
  | "installation_reminder"
  | "installation_in_progress"
  | "project_completed"
  | "change_order_approved"
  | "support_received";

export interface CustomerNotification {
  id: string;
  milestoneType: MilestoneType;
  title: string;
  message: string;
  timestamp: string;
  channels: ("email" | "sms")[];
  delivered: boolean;
  jobId: string;
  customerName: string;
}

/* ═══════════════════════════════════════════════════
   Customer-safe status translation
   ═══════════════════════════════════════════════════ */

const STAGE_TO_CUSTOMER: Record<JobStage, string> = {
  "Sale Closed":                        "Project Confirmed",
  "Review and Validation":              "Project in Preparation",
  "Review Complete":                    "Project in Preparation",
  "Ready for Ordering":                 "Project in Preparation",
  "Ordered":                            "Materials on the Way",
  "Permit and Order Pending":           "Project in Preparation",
  "Ready for Scheduling":               "Moving Toward Scheduling",
  "Scheduled Tentative":                "Installation Coming Up",
  "Scheduled Confirmed":                "Installation Scheduled",
  "Ready for Dispatch":                 "Installation Coming Up",
  "Available for Contractor Assignment": "Preparing Your Installation",
  "Assigned and Dispatched":            "Installation Coming Up",
  "In Progress":                        "Installation In Progress",
  "QA Review":                          "Quality Review",
  "Completed":                          "Project Complete",
  "Issue":                              "Being Reviewed",
};

export function getCustomerFriendlyStage(stage: JobStage): string {
  return STAGE_TO_CUSTOMER[stage] || "In Progress";
}

/** Map internal states to simple customer-safe descriptions using workflow engine order */
export function getCustomerStatusDescription(job: Job): string {
  const cfg = getStageConfig(job.currentStage);
  const order = cfg?.order ?? 0;

  if (order >= 100) {
    return "Your project has been completed. Thank you for choosing us!";
  }
  if (order >= 80) {
    return "Your installation is currently underway. Our team is on-site working on your project.";
  }
  if (job.scheduledInstallDate && order >= 55) {
    return `Your installation is scheduled for ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}. We're getting everything ready.`;
  }
  if (order >= 20 && order <= 45) {
    return "We are finalizing the details for your project. Everything is moving forward on schedule.";
  }
  if (order <= 10) {
    return "Your project has been confirmed and our team is beginning preparation.";
  }
  return "Your project is progressing. We'll keep you updated with any important changes.";
}

/* ═══════════════════════════════════════════════════
   Milestone notification templates
   ═══════════════════════════════════════════════════ */

interface MilestoneTemplate {
  type: MilestoneType;
  title: string;
  message: (firstName: string, job: Job) => string;
  channels: ("email" | "sms")[];
}

const MILESTONE_TEMPLATES: MilestoneTemplate[] = [
  {
    type: "contract_signed",
    title: "Project Confirmed",
    channels: ["email", "sms"],
    message: (name, job) =>
      `Congratulations, ${name}! Your ${job.projectType} project agreement has been signed. We're excited to get started. You can track your project anytime through your Customer Portal.`,
  },
  {
    type: "project_preparation",
    title: "Project in Preparation",
    channels: ["email"],
    message: (name, job) =>
      `Hi ${name}, your ${job.projectType} project is now in preparation. Our team is coordinating everything to ensure a smooth experience. We'll keep you posted as things progress.`,
  },
  {
    type: "materials_finalized",
    title: "Materials Being Prepared",
    channels: ["email"],
    message: (name) =>
      `Hi ${name}, the materials for your project are being prepared. We're making sure everything is ready for a great installation.`,
  },
  {
    type: "installation_scheduled",
    title: "Installation Scheduled",
    channels: ["email", "sms"],
    message: (name, job) => {
      const date = job.scheduledInstallDate
        ? new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
        : "soon";
      return `Great news, ${name}! Your installation has been scheduled for ${date}. We'll send you a reminder as the date approaches.`;
    },
  },
  {
    type: "installation_reminder",
    title: "Installation Reminder",
    channels: ["email", "sms"],
    message: (name, job) => {
      const date = job.scheduledInstallDate
        ? new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
        : "soon";
      const time = job.scheduledStartTime || "";
      return `Reminder: Your installation is ${date}${time ? ` — arrival window: ${time}` : ""}. Please ensure the work area is accessible. See you soon, ${name}!`;
    },
  },
  {
    type: "installation_in_progress",
    title: "Installation In Progress",
    channels: ["sms"],
    message: (name) =>
      `Hi ${name}, our crew has started your installation. We'll keep you updated through your portal as things progress!`,
  },
  {
    type: "project_completed",
    title: "Project Complete",
    channels: ["email", "sms"],
    message: (name, job) =>
      `${name}, your ${job.projectType} project is complete! Thank you for trusting us with your home. Please check your Customer Portal to view final documents and details.`,
  },
  {
    type: "change_order_approved",
    title: "Project Update Approved",
    channels: ["email"],
    message: (name) =>
      `Hi ${name}, a change to your project has been approved and your portal has been updated to reflect the latest details. Please review at your convenience.`,
  },
  {
    type: "support_received",
    title: "Support Request Received",
    channels: ["email"],
    message: (name) =>
      `Hi ${name}, we've received your support request and our team is reviewing it. We'll be in touch shortly. You can track the status in your Customer Portal.`,
  },
];

/* ═══════════════════════════════════════════════════
   Notification generation
   ═══════════════════════════════════════════════════ */

let _notifCounter = 0;

export function generateNotification(
  milestoneType: MilestoneType,
  job: Job,
): CustomerNotification | null {
  const template = MILESTONE_TEMPLATES.find(t => t.type === milestoneType);
  if (!template) return null;

  const firstName = job.customerName.split(" ")[0];
  _notifCounter++;

  return {
    id: `NOTIF-${Date.now().toString(36)}-${_notifCounter}`,
    milestoneType,
    title: template.title,
    message: template.message(firstName, job),
    timestamp: new Date().toISOString(),
    channels: template.channels.filter(ch => {
      if (ch === "email") return !!job.email;
      if (ch === "sms") return !!job.phone;
      return false;
    }),
    delivered: false,
    jobId: job.id,
    customerName: job.customerName,
  };
}

/** Generate a change order notification */
export function generateChangeOrderNotification(
  job: Job,
  _changeOrder: ChangeOrder,
): CustomerNotification | null {
  return generateNotification("change_order_approved", job);
}

/* ═══════════════════════════════════════════════════
   Derive milestone timeline from job state
   (reconstructs what milestones have been reached)
   ═══════════════════════════════════════════════════ */

export interface MilestoneEntry {
  type: MilestoneType;
  title: string;
  description: string;
  timestamp: string;
  reached: boolean;
}

/** Use workflow engine stage order for milestone progression */
export function buildMilestoneTimeline(job: Job, changeOrders: ChangeOrder[] = []): MilestoneEntry[] {
  const currentConfig = getStageConfig(job.currentStage);
  const currentOrder = currentConfig?.order ?? 0;
  const entries: MilestoneEntry[] = [];

  // 1. Contract signed
  entries.push({
    type: "contract_signed",
    title: "Project Confirmed",
    description: "Your agreement has been signed and your project is underway.",
    timestamp: job.contractDocument?.approvedAt || job.activityHistory?.[0]?.date || new Date().toISOString(),
    reached: currentOrder >= 10,
  });

  // 2. Project preparation
  entries.push({
    type: "project_preparation",
    title: "Project in Preparation",
    description: "Our team is coordinating permits, materials, and scheduling.",
    timestamp: currentOrder >= 20 ? (job.activityHistory?.find(a => a.action.includes("Validation") || a.action.includes("preparation"))?.date || "") : "",
    reached: currentOrder >= 20,
  });

  // 3. Materials
  entries.push({
    type: "materials_finalized",
    title: "Materials Being Prepared",
    description: "Your project materials are being prepared and organized.",
    timestamp: "",
    reached: currentOrder >= 40,
  });

  // 4. Installation scheduled
  const hasSchedule = !!job.scheduledInstallDate;
  entries.push({
    type: "installation_scheduled",
    title: "Installation Scheduled",
    description: hasSchedule
      ? `Scheduled for ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`
      : "Your installation date will be confirmed soon.",
    timestamp: hasSchedule ? job.scheduledInstallDate : "",
    reached: hasSchedule && currentOrder >= 55,
  });

  // 5. Installation in progress
  entries.push({
    type: "installation_in_progress",
    title: "Installation In Progress",
    description: "Our crew is on-site working on your project.",
    timestamp: "",
    reached: currentOrder >= 80,
  });

  // 6. Project completed
  entries.push({
    type: "project_completed",
    title: "Project Complete",
    description: "Your project has been completed. Thank you!",
    timestamp: "",
    reached: currentOrder >= 100,
  });

  // 7. Change orders (if any signed)
  const signedCOs = changeOrders.filter(co => co.status === "Signed");
  signedCOs.forEach(co => {
    entries.push({
      type: "change_order_approved",
      title: `Project Update #${co.orderNumber}`,
      description: co.summary || "A change to your project has been approved.",
      timestamp: co.signedAt || co.createdAt,
      reached: true,
    });
  });

  return entries;
}

/* ═══════════════════════════════════════════════════
   Simulated send (UI-only, logs to console)
   ═══════════════════════════════════════════════════ */

export function simulateSendNotification(notification: CustomerNotification): CustomerNotification {
  console.log(`[Customer Notification] Simulated send:`, {
    to: notification.customerName,
    channels: notification.channels,
    milestone: notification.milestoneType,
    title: notification.title,
  });
  return { ...notification, delivered: true };
}
